"use client";

import { useFormContext } from "react-hook-form";
import { useState, useCallback } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
} from "recharts";
import { calculateInstantSimulation } from "@/lib/life-simulation";
import { useDebouncedWatch } from "../hooks/use-debounced-watch";
import { useI18n } from "@/lib/i18n/hook";
import { useCurrency as useCurrencyContext } from "../contexts/currency-context";

export function ChartPreview() {
  const [chartData, setChartData] = useState<any[]>([]);
  const { t } = useI18n();
  const { currency } = useCurrencyContext();
  const updateChart = useCallback((formData: any) => {
    if (formData) {
      const data = calculateInstantSimulation(formData);
      setChartData(data);
    }
  }, []);

  useDebouncedWatch(updateChart);

  return (
    <Card>
      <CardHeader className="pb-2">
        <CardTitle className="text-lg">
          {t("lifePlan.result.chart.title")}
        </CardTitle>
      </CardHeader>
      <CardContent>
        <div className="h-[300px] w-full">
          {chartData.length > 0 ? (
            <ResponsiveContainer width="100%" height="100%">
              <LineChart
                data={chartData}
                margin={{
                  top: 5,
                  right: 5,
                  left: 5,
                  bottom: 5,
                }}
              >
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="age" />
                <YAxis
                  label={{
                    value: currency,
                    angle: -90,
                    position: "insideLeft",
                  }}
                />
                <Tooltip formatter={(value) => `${value.toLocaleString()}`} />
                <Legend />
                <Line
                  type="monotone"
                  dataKey="assets"
                  name={t("lifePlan.result.chart.assets")}
                  stroke="#8884d8"
                  activeDot={{ r: 8 }}
                />
                <Line
                  type="monotone"
                  dataKey="income"
                  name={t("lifePlan.result.chart.income")}
                  stroke="#82ca9d"
                />
                <Line
                  type="monotone"
                  dataKey="expense"
                  name={t("lifePlan.result.chart.expense")}
                  stroke="#ff7300"
                />
              </LineChart>
            </ResponsiveContainer>
          ) : (
            <div className="flex h-full items-center justify-center">
              <p className="text-muted-foreground">
                {t("lifePlan.result.chart.noData")}
              </p>
            </div>
          )}
        </div>
      </CardContent>
    </Card>
  );
}
